import { createSelector } from "@ngrx/store";
import { AppState } from "src/app/shared/store/app.state";

import {
  Beneficiary,
  Gender,
  QuoteFeatureState,
  UnderwritingQuestion,
  UnderwritingQuestionAnswer,
} from "./quote.state";

export const selectFeatureState = (state: AppState) =>
  state.termLifeInsuranceQuote;

export const selectQuoteId = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.quoteId,
);

export const selectIsBusy = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.isBusy,
);

export const selectIsLegacyApplication = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.isLegacyApplication,
);

export const selectApplicationId = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.applicationId,
);

export const selectEmail = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.email,
);

export const selectAcceptedTerms = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.hasTermsAccepted,
);

export const selectGender = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.gender,
);

export const selectDateOfBirth = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.dateOfBirth,
);

export const selectIsSmoker = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.isSmoker,
);

export const selectInsuredSum = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.insuredSum,
);

export const selectInsuranceTermInYears = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.insuranceTermInYears,
);

export const selectBmiConfiguration = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.productConfiguration?.bmiConfiguration,
);

export const conversionCapitalDevelopmentsInAscendingOrder = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) =>
    state.conversionCapitalDevelopments
      ?.slice()
      .sort((left, right) => left.date.getTime() - right.date.getTime()),
);

export const selectProductConfiguration = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.productConfiguration,
);

export const selectTechnicalAgeInYears = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => state.technicalAgeInYears,
);

export const selectRetirementAge = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => {
    if (
      !state.gender ||
      !state.technicalAgeInYears ||
      !state.productConfiguration
    ) {
      return undefined;
    }

    const statutoryRetirementAge =
      state.gender === Gender.FEMALE
        ? state.productConfiguration.statutoryRetirementAgeFemale
        : state.productConfiguration.statutoryRetirementAgeMale;

    // For simplicity we use the technical age. Check with Chris if this is good enough
    const numberOfYearsUntilStatutoryRetirement =
      statutoryRetirementAge - state.technicalAgeInYears;

    return numberOfYearsUntilStatutoryRetirement;
  },
);

export const underwritingQuestionsState = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => {
    const underwritingState: UnderwritingQuestionsState = {
      hasUnderwritingQuestionsPassed: state.hasUnderwritingQuestionsPassed,
      underwritingQuestions: state.underwritingQuestions,
      underwritingQuestionAnswers: state.answeredUnderwritingQuestions,
    };

    return underwritingState;
  },
);

export const selectBeneficiaryState = createSelector(
  selectFeatureState,
  (state: QuoteFeatureState) => {
    const beneficiaryState: BeneficiaryState = {
      useStandardBeneficiaries: state.useStandardBeneficiaries,
      beneficiaries: state.beneficiaries,
    };

    return beneficiaryState;
  },
);

export interface UnderwritingQuestionsState {
  hasUnderwritingQuestionsPassed: boolean | undefined | null;
  underwritingQuestions: UnderwritingQuestion[] | undefined;
  underwritingQuestionAnswers: UnderwritingQuestionAnswer[] | undefined | null;
}

export interface BeneficiaryState {
  useStandardBeneficiaries?: boolean | undefined;
  beneficiaries?: Beneficiary[] | null | undefined;
}
